import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, Moon, Sun, Bookmark, ZoomIn, ZoomOut, X, Loader2, Maximize2, Minimize2, Smartphone, Monitor, Coffee, RotateCcw, ShoppingCart, Download, Share2, MessageCircle } from 'lucide-react';
import { gamificationMessages } from '../data/bookContent';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker - use unpkg for better compatibility
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PDF_URL = '/books/cabala-para-principiantes.pdf';
const BASE_FREE_PAGES = 30;
const BONUS_FREE_PAGES = 40; // After level 3 reward
const MINUTES_PER_PAGE = 1.5; // Average reading time

// Theme configurations
const themes = {
  light: {
    bg: 'bg-white',
    text: 'text-gray-900',
    secondary: 'text-gray-500',
    card: 'bg-white',
    border: 'border-gray-200',
    hover: 'hover:bg-gray-100',
    pdfFilter: 'none'
  },
  sepia: {
    bg: 'bg-[#f4ecd8]',
    text: 'text-[#5c4b37]',
    secondary: 'text-[#8b7355]',
    card: 'bg-[#faf6ed]',
    border: 'border-[#d4c4a8]',
    hover: 'hover:bg-[#ebe3d0]',
    pdfFilter: 'sepia(20%) brightness(0.95)'
  },
  dark: {
    bg: 'bg-[#121212]',
    text: 'text-gray-100',
    secondary: 'text-gray-400',
    card: 'bg-[#1e1e1e]',
    border: 'border-gray-800',
    hover: 'hover:bg-gray-800',
    pdfFilter: 'invert(0.9) hue-rotate(180deg)'
  }
};

export const ReaderPage = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [theme, setTheme] = useState('light');
  // Default zoom: 70% for PC, 120% for mobile
  const [scale, setScale] = useState(() => window.innerWidth >= 768 ? 0.7 : 1.2);
  const [bookmarks, setBookmarks] = useState([]);
  const [showPaywall, setShowPaywall] = useState(false);
  const [userProgress, setUserProgress] = useState(null);
  const [showGamification, setShowGamification] = useState(false);
  const [currentBadge, setCurrentBadge] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isImmersive, setIsImmersive] = useState(false);
  const [containerWidth, setContainerWidth] = useState(800);
  const [showControls, setShowControls] = useState(true);
  const [showOrientationTip, setShowOrientationTip] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState('');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [hasBonusPages, setHasBonusPages] = useState(() => {
    return localStorage.getItem('reader_bonus_pages') === 'true';
  });
  const hideControlsTimeout = useRef(null);

  const currentTheme = themes[theme];
  const WHATSAPP_NUMBER = '+5492604801102';
  const FREE_PAGES_LIMIT = hasBonusPages ? BONUS_FREE_PAGES : BASE_FREE_PAGES;

  // Auth check
  useEffect(() => {
    const token = localStorage.getItem('reader_token');
    if (!token) {
      navigate('/reader/login');
      return;
    }
    loadUserProgress();
  }, [navigate]);

  // Orientation tip
  useEffect(() => {
    const hasSeenTip = localStorage.getItem('reader_orientation_tip');
    if (!hasSeenTip && !pdfLoading) {
      setTimeout(() => setShowOrientationTip(true), 1000);
    }
  }, [pdfLoading]);

  // Container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = Math.min(containerRef.current.offsetWidth - 20, 850);
        setContainerWidth(width);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showPaywall || showGamification || showOrientationTip) return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goToPage(currentPage + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPage(currentPage - 1);
      } else if (e.key === 'Escape' && (isFullscreen || isImmersive)) {
        if (document.fullscreenElement && document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        }
        setIsImmersive(false);
        setIsFullscreen(false);
        setShowControls(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, isFullscreen, isImmersive, showPaywall, showGamification, showOrientationTip, userProgress]);

  // Auto-hide controls
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
      hideControlsTimeout.current = setTimeout(() => {
        if (!showPaywall && !showGamification && !showOrientationTip) {
          setShowControls(false);
        }
      }, 3500);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchstart', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchstart', handleMouseMove);
      if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
    };
  }, [showPaywall, showGamification, showOrientationTip]);

  // Touch swipe for mobile - improved to avoid conflict with pinch zoom
  useEffect(() => {
    let touchStartX = null;
    let touchStartY = null;
    let touchStartTime = null;
    let isSingleTouch = false;

    const handleTouchStart = (e) => {
      // Only track single finger touches (not pinch zoom)
      if (e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartTime = Date.now();
        isSingleTouch = true;
      } else {
        // Multi-touch (pinch zoom) - ignore
        isSingleTouch = false;
        touchStartX = null;
      }
    };

    const handleTouchMove = (e) => {
      // If more than one finger, cancel swipe detection
      if (e.touches.length > 1) {
        isSingleTouch = false;
        touchStartX = null;
      }
    };

    const handleTouchEnd = (e) => {
      if (!isSingleTouch || touchStartX === null) return;
      
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const timeDiff = Date.now() - touchStartTime;
      
      const diffX = touchStartX - touchEndX;
      const diffY = Math.abs(touchStartY - touchEndY);
      
      // Only trigger swipe if:
      // 1. Horizontal movement > 100px (less sensitive)
      // 2. Vertical movement < 80px (mostly horizontal)
      // 3. Swipe took less than 500ms (intentional quick swipe)
      // 4. Was a single touch throughout
      if (Math.abs(diffX) > 100 && diffY < 80 && timeDiff < 500) {
        if (diffX > 0) goToPage(currentPage + 1);
        else goToPage(currentPage - 1);
      }
      
      touchStartX = null;
      touchStartY = null;
      isSingleTouch = false;
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
      container.addEventListener('touchmove', handleTouchMove, { passive: true });
      container.addEventListener('touchend', handleTouchEnd, { passive: true });
    }
    return () => {
      if (container) {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [currentPage, userProgress]);

  const loadUserProgress = async () => {
    try {
      const token = localStorage.getItem('reader_token');
      const response = await axios.get(`${API}/reader/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProgress(response.data);
      if (response.data.lastPage > 1) setCurrentPage(response.data.lastPage);
      setBookmarks(response.data.bookmarks || []);
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const saveProgress = async (page) => {
    try {
      const token = localStorage.getItem('reader_token');
      await axios.post(`${API}/reader/progress`, { page, bookmarks }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  }, []);

  const goToPage = useCallback((page) => {
    if (page < 1 || (numPages && page > numPages) || isTransitioning) return;
    
    const currentFreeLimit = hasBonusPages ? BONUS_FREE_PAGES : BASE_FREE_PAGES;
    if (page > currentFreeLimit && !userProgress?.hasPaid) {
      setShowPaywall(true);
      return;
    }

    // Smooth transition
    setTransitionDirection(page > currentPage ? 'next' : 'prev');
    setIsTransitioning(true);
    
    setTimeout(() => {
      setCurrentPage(page);
      saveProgress(page);
      setIsTransitioning(false);
      setTransitionDirection('');

      // Gamification - Solo mostrar si el usuario NO tiene acceso completo (no pagó ni usó código)
      if (gamificationMessages[page] && !userProgress?.hasPaid) {
        const badge = gamificationMessages[page];
        
        // Check if this badge gives bonus pages (level 3)
        if (badge.reward === 'extra_pages' && !hasBonusPages) {
          setHasBonusPages(true);
          localStorage.setItem('reader_bonus_pages', 'true');
        }
        
        setTimeout(() => {
          setCurrentBadge(badge);
          setShowGamification(true);
        }, 400);
      }

      // Auto paywall at limit (use current FREE_PAGES_LIMIT which may have been updated)
      const currentLimit = hasBonusPages ? BONUS_FREE_PAGES : BASE_FREE_PAGES;
      if (page === currentLimit && !userProgress?.hasPaid) {
        setTimeout(() => setShowPaywall(true), 800);
      }

      // Check if book is completed
      checkBookCompletion(page);
    }, 200);
  }, [currentPage, numPages, userProgress, isTransitioning, bookmarks, hasBonusPages]);

  const handleTapNavigation = (e) => {
    if (showPaywall || showGamification || showOrientationTip || showCompletionModal || showWhatsAppModal) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    
    if (x < width * 0.3) {
      goToPage(currentPage - 1);
    } else if (x > width * 0.7) {
      goToPage(currentPage + 1);
    } else {
      setShowControls(true);
    }
  };

  const toggleBookmark = () => {
    const newBookmarks = bookmarks.includes(currentPage)
      ? bookmarks.filter(p => p !== currentPage)
      : [...bookmarks, currentPage];
    setBookmarks(newBookmarks);
    toast.success(bookmarks.includes(currentPage) ? 'Marcador eliminado' : '¡Página guardada!');
  };

  const handleShareWhatsApp = () => {
    const shareUrl = `${window.location.origin}/reader/register`;
    const message = `¡Hola! Te recomiendo este libro: "Cábala para principiantes" de Raíz Lecturas. Podés leer las primeras 30 páginas gratis acá: ${shareUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('¡Gracias por compartir!');
  };

  const adjustScale = (direction) => {
    setScale(s => direction === 'in' ? Math.min(2, s + 0.15) : Math.max(0.6, s - 0.15));
  };

  const cycleTheme = () => {
    const themeOrder = ['light', 'sepia', 'dark'];
    const currentIndex = themeOrder.indexOf(theme);
    setTheme(themeOrder[(currentIndex + 1) % themeOrder.length]);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current || document.documentElement;
    const isNativeActive =
      document.fullscreenElement || document.webkitFullscreenElement;

    if (!isImmersive) {
      // Try the native Fullscreen API (works on Android/desktop, not iOS Safari)
      const req =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.mozRequestFullScreen ||
        el.msRequestFullscreen;
      if (req) {
        try {
          Promise.resolve(req.call(el)).catch(() => {});
        } catch (err) {
          // ignore – iOS will just use the CSS immersive mode below
        }
      }
      // CSS immersive mode – this is what makes it "fullscreen" on iPhone
      setIsImmersive(true);
      setIsFullscreen(true);
      setShowControls(false);
      // Recalculate PDF width for the new available space
      setTimeout(() => window.dispatchEvent(new Event('resize')), 60);
    } else {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;
      if (isNativeActive && exit) {
        try {
          Promise.resolve(exit.call(document)).catch(() => {});
        } catch (err) {
          // ignore
        }
      }
      setIsImmersive(false);
      setIsFullscreen(false);
      setShowControls(true);
      setTimeout(() => window.dispatchEvent(new Event('resize')), 60);
    }
  };

  const dismissOrientationTip = (tryHorizontal = false) => {
    setShowOrientationTip(false);
    localStorage.setItem('reader_orientation_tip', 'true');
    
    if (tryHorizontal) {
      requestLandscapeMode();
    }
  };

  // Request landscape/horizontal mode
  const requestLandscapeMode = async () => {
    try {
      // First try to go fullscreen (required for orientation lock on most browsers)
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      }
      
      // Then try to lock orientation to landscape
      if (screen.orientation && screen.orientation.lock) {
        await screen.orientation.lock('landscape');
        toast.success('📱 Modo horizontal activado');
        return;
      }
    } catch (error) {
      console.log('Orientation lock not supported:', error);
    }
    
    // If orientation lock failed, show helpful message
    toast.info(
      '📱 Desbloqueá la rotación en tu celular y giralo horizontal',
      { 
        duration: 5000,
        description: 'Deslizá desde arriba y desactivá el candado de rotación'
      }
    );
  };

  const maxPages = userProgress?.hasPaid ? (numPages || 999) : FREE_PAGES_LIMIT;
  const progress = Math.round((currentPage / maxPages) * 100);
  const remainingPages = maxPages - currentPage;
  const remainingMinutes = Math.ceil(remainingPages * MINUTES_PER_PAGE);

  // Download PDF function for paid users
  const handleDownloadPDF = async () => {
    const token = localStorage.getItem('reader_token');
    if (!token) {
      toast.error('Sesión expirada. Por favor, volvé a iniciar sesión.');
      return;
    }
    
    toast.info('Preparando descarga...');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/reader/download-book`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error al descargar');
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Cabala-para-principiantes.pdf';
      link.style.display = 'none';
      
      // For iOS Safari compatibility
      if (navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
        // Open in new tab for iOS
        window.open(url, '_blank');
        toast.success('📥 PDF abierto en nueva pestaña. Usá el botón de compartir para guardar.');
      } else {
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('📥 Descarga iniciada');
      }
      
      // Cleanup
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      
      // Show WhatsApp modal after download
      setTimeout(() => setShowWhatsAppModal(true), 2000);
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Error al descargar el PDF');
    }
  };

  // Check if user reached the last page
  const checkBookCompletion = (page) => {
    if (numPages && page >= numPages && userProgress?.hasPaid) {
      // User finished the book!
      setTimeout(() => setShowCompletionModal(true), 1000);
    }
  };

  // Open WhatsApp with pre-filled message
  const openWhatsApp = (message) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div 
      ref={containerRef}
      className={`transition-colors duration-500 ${currentTheme.bg} ${
        isImmersive ? 'fixed inset-0 z-[55] overflow-y-auto overscroll-none' : 'min-h-screen'
      }`}
      style={isImmersive ? { height: '100dvh', width: '100vw' } : undefined}
    >
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-400 ${
          showControls ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        } ${currentTheme.card}/95 backdrop-blur-lg border-b ${currentTheme.border}`}
      >
        <div className="max-w-5xl mx-auto px-3 py-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/reader/biblioteca')} 
                className={`p-2 rounded-full ${currentTheme.hover} ${currentTheme.secondary}`}
                title="Ir a Mi Biblioteca"
              >
                <X size={20} />
              </button>
              <div className="hidden sm:block">
                <h1 className={`text-base font-serif font-semibold ${currentTheme.text}`}>
                  Cábala para principiantes
                </h1>
                <p className={`text-xs ${currentTheme.secondary}`}>Agustín Mata</p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              {/* Zoom */}
              <div className={`hidden sm:flex items-center gap-0.5 px-2 py-1 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : theme === 'sepia' ? 'bg-[#e8dcc4]' : 'bg-gray-100'}`}>
                <button onClick={() => adjustScale('out')} className={`p-1.5 rounded ${currentTheme.hover} ${currentTheme.secondary}`}>
                  <ZoomOut size={16} />
                </button>
                <span className={`text-xs w-10 text-center ${currentTheme.secondary}`}>{Math.round(scale * 100)}%</span>
                <button onClick={() => adjustScale('in')} className={`p-1.5 rounded ${currentTheme.hover} ${currentTheme.secondary}`}>
                  <ZoomIn size={16} />
                </button>
              </div>

              {/* Bookmark */}
              <button 
                onClick={toggleBookmark}
                className={`p-2 rounded-full transition-all ${
                  bookmarks.includes(currentPage) ? 'text-amber-500 bg-amber-500/10' : `${currentTheme.hover} ${currentTheme.secondary}`
                }`}
              >
                <Bookmark size={18} fill={bookmarks.includes(currentPage) ? 'currentColor' : 'none'} />
              </button>

              {/* Theme toggle */}
              <button onClick={cycleTheme} className={`p-2 rounded-full ${currentTheme.hover} ${currentTheme.secondary}`}>
                {theme === 'dark' ? <Sun size={18} /> : theme === 'sepia' ? <Coffee size={18} /> : <Moon size={18} />}
              </button>

              {/* Rotate/Landscape button - mobile only */}
              <button 
                onClick={requestLandscapeMode} 
                className={`sm:hidden p-2 rounded-full ${currentTheme.hover} ${currentTheme.secondary}`}
                title="Modo horizontal"
              >
                <RotateCcw size={18} />
              </button>

              {/* Fullscreen - all devices (immersive mode on iOS) */}
              <button onClick={toggleFullscreen} title="Pantalla completa" className={`block p-2 rounded-full ${currentTheme.hover} ${currentTheme.secondary}`}>
                {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>

              {/* Share WhatsApp */}
              <button 
                onClick={handleShareWhatsApp}
                className="p-2 rounded-full bg-green-600 hover:bg-green-500 text-white transition-all"
                title="Compartir en WhatsApp"
                data-testid="reader-share-whatsapp-btn"
              >
                <Share2 size={18} />
              </button>
            </div>
          </div>

          {/* Progress */}
          <div className="mt-2">
            <div className={`w-full h-1 rounded-full ${theme === 'dark' ? 'bg-gray-800' : theme === 'sepia' ? 'bg-[#d4c4a8]' : 'bg-gray-200'}`}>
              <div 
                className="h-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className={`flex justify-between items-center mt-1 text-xs ${currentTheme.secondary}`}>
              <span>Página {currentPage} / {maxPages}</span>
              <span>{remainingMinutes} min restantes</span>
            </div>
          </div>
        </div>
      </header>

      {/* Floating exit button - visible in immersive mode so mobile users can always exit */}
      {isImmersive && (
        <button
          onClick={toggleFullscreen}
          title="Salir de pantalla completa"
          className="fixed top-3 right-3 z-[60] p-2.5 rounded-full bg-black/55 text-white backdrop-blur-md shadow-lg active:scale-95 transition-transform"
        >
          <Minimize2 size={20} />
        </button>
      )}

      {/* PDF Area - Tap zones */}
      <main 
        className="pt-24 pb-28 min-h-screen flex justify-center items-start cursor-pointer select-none"
        onClick={handleTapNavigation}
      >
        {/* Left tap zone indicator */}
        <div className={`fixed left-0 top-24 bottom-28 w-[30%] z-10 flex items-center justify-start pl-4 opacity-0 hover:opacity-100 transition-opacity pointer-events-none`}>
          <div className={`p-3 rounded-full ${theme === 'dark' ? 'bg-white/10' : 'bg-black/5'}`}>
            <ChevronLeft size={24} className={currentTheme.secondary} />
          </div>
        </div>
        
        {/* Right tap zone indicator */}
        <div className={`fixed right-0 top-24 bottom-28 w-[30%] z-10 flex items-center justify-end pr-4 opacity-0 hover:opacity-100 transition-opacity pointer-events-none`}>
          <div className={`p-3 rounded-full ${theme === 'dark' ? 'bg-white/10' : 'bg-black/5'}`}>
            <ChevronRight size={24} className={currentTheme.secondary} />
          </div>
        </div>

        <div className={`relative transition-all duration-300 ${
          isTransitioning 
            ? transitionDirection === 'next' 
              ? 'translate-x-[-30px] opacity-0' 
              : 'translate-x-[30px] opacity-0'
            : 'translate-x-0 opacity-100'
        }`}>
          {pdfLoading && (
            <div className="flex flex-col items-center justify-center py-32">
              <Loader2 className="animate-spin text-amber-500" size={40} />
              <p className={`mt-4 ${currentTheme.secondary}`}>Cargando libro...</p>
            </div>
          )}

          <div 
            className="pdf-wrapper rounded-lg overflow-hidden shadow-2xl"
            style={{ filter: currentTheme.pdfFilter }}
          >
            <Document
              file={PDF_URL}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(e) => console.error('PDF Error:', e)}
              loading={null}
            >
              <Page
                pageNumber={currentPage}
                width={containerWidth * scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </div>
        </div>
      </main>

      {/* Bottom Nav */}
      <nav 
        className={`fixed bottom-0 left-0 right-0 z-40 transition-all duration-400 ${
          showControls ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        } ${currentTheme.card}/95 backdrop-blur-lg border-t ${currentTheme.border}`}
      >
        <div className="max-w-xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full font-medium transition-all disabled:opacity-30 ${
                theme === 'dark' ? 'bg-gray-800 text-white' : theme === 'sepia' ? 'bg-[#e8dcc4] text-[#5c4b37]' : 'bg-gray-100 text-gray-800'
              }`}
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline text-sm">Anterior</span>
            </button>

            <div className={`flex items-center gap-2 ${currentTheme.text}`}>
              <button 
                onClick={() => goToPage(Math.max(1, currentPage - 5))} 
                disabled={currentPage <= 5}
                className={`text-xs px-2 py-1 rounded ${currentTheme.hover} disabled:opacity-30 ${currentTheme.secondary}`}
              >
                -5
              </button>
              <input
                type="number"
                value={currentPage}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val)) goToPage(val);
                }}
                className={`w-12 text-center py-1.5 rounded-lg text-sm font-medium border focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                  theme === 'dark' ? 'bg-gray-800 border-gray-700 text-white' : theme === 'sepia' ? 'bg-[#faf6ed] border-[#d4c4a8] text-[#5c4b37]' : 'bg-white border-gray-200'
                }`}
                min="1"
                max={maxPages}
              />
              <span className={`text-sm ${currentTheme.secondary}`}>/ {maxPages}</span>
              <button 
                onClick={() => goToPage(Math.min(maxPages, currentPage + 5))} 
                disabled={currentPage + 5 > maxPages}
                className={`text-xs px-2 py-1 rounded ${currentTheme.hover} disabled:opacity-30 ${currentTheme.secondary}`}
              >
                +5
              </button>
            </div>

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= maxPages}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full font-medium bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white transition-all disabled:opacity-30 shadow-lg shadow-amber-500/20"
            >
              <span className="hidden sm:inline text-sm">Siguiente</span>
              <ChevronRight size={18} />
            </button>
          </div>
          <p className={`text-center text-xs mt-2 ${currentTheme.secondary}`}>
            Toca los lados o desliza para navegar
          </p>
        </div>
      </nav>

      {/* Gamification */}
      {showGamification && currentBadge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <Card className="bg-white shadow-2xl max-w-sm w-full animate-scale-in overflow-hidden">
            {/* Level indicator bar */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 flex items-center justify-center gap-2">
              <span className="text-white text-sm font-bold">
                {currentBadge.level === "MAX" ? "🏆 NIVEL MÁXIMO" : `NIVEL ${currentBadge.level}`}
              </span>
            </div>
            <button onClick={() => setShowGamification(false)} className="absolute top-2 right-2 p-1.5 hover:bg-white/20 rounded-full">
              <X size={18} className="text-white" />
            </button>
            <CardContent className="p-8 text-center">
              <div className="text-6xl mb-4 animate-bounce-once">{currentBadge.badge}</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{currentBadge.title}</h3>
              <p className="text-gray-600 leading-relaxed">{currentBadge.message}</p>
              
              {/* Progress bar visual */}
              <div className="mt-5 mb-2">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>Progreso</span>
                  <span>{currentBadge.level === "MAX" ? "100" : Math.round((currentBadge.level / 6) * 100)}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500"
                    style={{ width: currentBadge.level === "MAX" ? "100%" : `${(currentBadge.level / 6) * 100}%` }}
                  />
                </div>
              </div>
              
              <button onClick={() => setShowGamification(false)} className="mt-4 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full font-medium hover:from-amber-600 hover:to-orange-600 shadow-lg shadow-amber-500/20">
                ¡Seguir leyendo!
              </button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Paywall */}
      {showPaywall && <PaywallModal 
        onClose={() => setShowPaywall(false)} 
        theme={theme} 
        currentPage={currentPage}
        freePageLimit={FREE_PAGES_LIMIT}
        onCodeSuccess={() => {
          loadUserProgress(); // Reload to get updated hasPaid status
        }}
      />}

      {/* Purchase Modal - for users who want to buy anytime */}
      {showPurchaseModal && <PurchaseInfoModal 
        onClose={() => setShowPurchaseModal(false)} 
        theme={theme}
        hasPaid={userProgress?.hasPaid}
        onDownload={handleDownloadPDF}
        onCodeSuccess={() => {
          loadUserProgress();
        }}
      />}

      {/* Floating Purchase/Download Button */}
      {showControls && (
        <div className={`fixed right-4 bottom-28 z-30 transition-all duration-300 ${
          showControls ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          {userProgress?.hasPaid ? (
            <button
              onClick={() => setShowPurchaseModal(true)}
              data-testid="download-book-btn"
              className="flex items-center gap-2 px-4 py-3 rounded-full font-semibold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg shadow-green-500/30 transition-all animate-pulse-slow"
            >
              <Download size={18} />
              <span className="hidden sm:inline">Descargar PDF</span>
            </button>
          ) : (
            <button
              onClick={() => setShowPurchaseModal(true)}
              data-testid="purchase-book-btn"
              className="flex items-center gap-2 px-4 py-3 rounded-full font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/30 transition-all"
            >
              <ShoppingCart size={18} />
              <span className="hidden sm:inline">Adquirir libro completo</span>
            </button>
          )}
        </div>
      )}

      {/* Orientation Tip */}
      {showOrientationTip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <Card className="max-w-md w-full bg-white shadow-2xl animate-scale-in">
            <CardContent className="p-6">
              <div className="text-center mb-5">
                <div className="text-4xl mb-2">📖</div>
                <h3 className="text-xl font-bold text-gray-900">¡Bienvenido!</h3>
                <p className="text-gray-500 text-sm mt-1">Para mejor lectura:</p>
              </div>
              <div className="space-y-3 mb-4">
                <button 
                  onClick={() => dismissOrientationTip(true)} 
                  className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 transition-all"
                >
                  <Monitor size={28} className="text-amber-600 flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-amber-700 block">Girá tu celular</span>
                    <span className="text-xs text-amber-600">Modo horizontal recomendado ✨</span>
                  </div>
                </button>
                <button 
                  onClick={() => dismissOrientationTip(false)} 
                  className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
                >
                  <Smartphone size={28} className="text-gray-400 flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-medium text-gray-700 block">Mantener vertical</span>
                    <span className="text-xs text-gray-400">Seguir como está</span>
                  </div>
                </button>
              </div>
              <p className="text-center text-xs text-gray-400">Tip: Deslizá o tocá los lados para pasar páginas</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Book Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <Card className={`max-w-md w-full relative animate-scale-in ${theme === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white'}`}>
            <button 
              onClick={() => setShowCompletionModal(false)} 
              className={`absolute top-3 right-3 p-2 rounded-full ${theme === 'dark' ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <X size={18} />
            </button>
            <CardContent className="p-6">
              <div className="text-center mb-5">
                <div className="text-6xl mb-3">🎉</div>
                <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  ¡Felicitaciones!
                </h2>
                <p className={`text-base mt-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  Terminaste "Cábala para principiantes"
                </p>
              </div>
              
              <div className={`p-4 rounded-xl mb-5 ${theme === 'dark' ? 'bg-amber-900/20 border border-amber-800' : 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200'}`}>
                <p className={`text-sm text-center ${theme === 'dark' ? 'text-amber-300' : 'text-amber-700'}`}>
                  Tu opinión es muy importante para nosotros. ¿Nos contás qué te pareció?
                </p>
              </div>
              
              <div className="space-y-3">
                <button 
                  onClick={() => {
                    openWhatsApp('¡Hola! Terminé de leer "Cábala para principiantes" y quiero dejar mi opinión: ');
                    setShowCompletionModal(false);
                  }}
                  data-testid="review-whatsapp-btn"
                  className="w-full py-4 rounded-full font-bold text-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-xl shadow-green-500/30 transition-all flex items-center justify-center gap-2"
                >
                  <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Dejar mi opinión
                </button>
                
                <button 
                  onClick={() => {
                    openWhatsApp('¡Hola! Terminé "Cábala para principiantes" y quiero enterarme de nuevos libros de Raíz Lecturas.');
                    setShowCompletionModal(false);
                  }}
                  data-testid="new-books-whatsapp-btn"
                  className={`w-full py-3 rounded-full font-medium border-2 ${theme === 'dark' ? 'border-green-700 text-green-400 hover:bg-green-900/30' : 'border-green-500 text-green-600 hover:bg-green-50'} transition-all flex items-center justify-center gap-2`}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Enterarme de nuevos libros
                </button>
              </div>
              
              <button 
                onClick={() => setShowCompletionModal(false)} 
                className={`w-full py-2 mt-4 text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}
              >
                Quizás más tarde
              </button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* WhatsApp Modal - After PDF Download */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <Card className={`max-w-md w-full relative animate-scale-in ${theme === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white'}`}>
            <button 
              onClick={() => setShowWhatsAppModal(false)} 
              className={`absolute top-3 right-3 p-2 rounded-full ${theme === 'dark' ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <X size={18} />
            </button>
            <CardContent className="p-6">
              <div className="text-center mb-5">
                <div className="text-5xl mb-3">📚</div>
                <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  ¡Descarga exitosa!
                </h2>
                <p className={`text-base mt-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  Ya podés leer el libro en tu Kindle o dispositivo favorito
                </p>
              </div>
              
              <div className={`p-4 rounded-xl mb-5 ${theme === 'dark' ? 'bg-green-900/20 border border-green-800' : 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'}`}>
                <p className={`text-sm text-center ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                  ¿Querés enterarte cuando publiquemos nuevos libros?
                </p>
              </div>
              
              <button 
                onClick={() => {
                  openWhatsApp('¡Hola! Descargué "Cábala para principiantes" y quiero enterarme de nuevos libros de Raíz Lecturas.');
                  setShowWhatsAppModal(false);
                }}
                data-testid="whatsapp-new-books-btn"
                className="w-full py-4 rounded-full font-bold text-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-xl shadow-green-500/30 transition-all flex items-center justify-center gap-2"
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Sí, quiero enterarme
              </button>
              
              <button 
                onClick={() => setShowWhatsAppModal(false)} 
                className={`w-full py-2 mt-3 text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}
              >
                No, gracias
              </button>
            </CardContent>
          </Card>
        </div>
      )}

      <style>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scale-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes bounce-once { 
          0%, 100% { transform: translateY(0); }
          25% { transform: translateY(-15px); }
          50% { transform: translateY(0); }
          75% { transform: translateY(-8px); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        .animate-fade-in { animation: fade-in 0.25s ease-out; }
        .animate-scale-in { animation: scale-in 0.25s ease-out; }
        .animate-bounce-once { animation: bounce-once 0.6s ease-out; }
        .animate-pulse-slow { animation: pulse-slow 2s ease-in-out infinite; }
        .pdf-wrapper canvas { display: block; margin: 0 auto; }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
};

// Paywall
const PaywallModal = ({ onClose, theme, currentPage, freePageLimit, onCodeSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const isDark = theme === 'dark';
  const isAtLimit = currentPage >= freePageLimit;
  
  const handlePurchase = () => {
    const message = 'Quiero comprar el libro Kábala para principiantes Raíz lectura';
    const url = `https://wa.me/5492604801102?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleRedeemCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Ingresá un código');
      return;
    }
    setCodeLoading(true);
    try {
      const token = localStorage.getItem('reader_token');
      const response = await axios.post(`${BACKEND_URL}/api/reader/redeem-code`, 
        { code: promoCode.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success('🎉 ¡Código canjeado! Libro desbloqueado');
        if (onCodeSuccess) onCodeSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setCodeLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <Card className={`max-w-md w-full relative animate-scale-in ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white'}`}>
        {/* Only show close button if NOT at the limit */}
        {!isAtLimit && (
          <button onClick={onClose} className={`absolute top-3 right-3 p-2 rounded-full ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <X size={18} />
          </button>
        )}
        <CardContent className="p-6">
          <div className="text-center mb-5">
            {isAtLimit ? (
              <>
                <div className="text-5xl mb-3">🎉</div>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  ¡Llegaste al final del preview!
                </h2>
                <p className={`text-base mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Leíste las 30 páginas gratis. Para continuar, desbloqueá el libro completo.
                </p>
              </>
            ) : (
              <>
                <div className="text-4xl mb-2">📚</div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>¿Te está gustando?</h2>
                <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Página {currentPage} de {freePageLimit}</p>
              </>
            )}
          </div>
          
          <div className={`p-5 rounded-xl mb-5 ${isDark ? 'bg-amber-900/20 border border-amber-800' : 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200'}`}>
            <div className="flex justify-between items-center mb-3">
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Libro Completo</span>
              <div className="text-right">
                <span className="text-3xl font-bold text-amber-500">$15</span>
                <span className={`text-sm ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>USD</span>
              </div>
            </div>
            
            {/* Total pages highlight */}
            <div className={`text-center py-2 mb-3 rounded-lg ${isDark ? 'bg-amber-800/30' : 'bg-amber-100'}`}>
              <span className={`text-2xl font-bold ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>📖 176 páginas</span>
              <p className={`text-xs ${isDark ? 'text-amber-300/70' : 'text-amber-600'}`}>de conocimiento profundo</p>
            </div>
            
            <ul className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> Acceso a las 176 páginas completas
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> Descarga en PDF para Kindle
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> Soporte por WhatsApp
              </li>
            </ul>
          </div>
          
          <button 
            onClick={handlePurchase} 
            className="w-full py-4 rounded-full font-bold text-lg bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white shadow-xl shadow-green-500/30 transition-all flex items-center justify-center gap-2"
          >
            <MessageCircle size={22} /> Comprar por WhatsApp
          </button>

          {/* Promo Code Section */}
          {!showCodeInput ? (
            <button 
              onClick={() => setShowCodeInput(true)} 
              className={`w-full py-3 mt-4 text-sm font-medium ${isDark ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-700'}`}
            >
              🎁 ¿Tenés un código de regalo?
            </button>
          ) : (
            <div className="mt-4 space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="CÓDIGO"
                  className={`flex-1 px-4 py-3 rounded-lg text-center font-mono uppercase tracking-wider border ${
                    isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-amber-500`}
                  maxLength={20}
                />
                <button
                  onClick={handleRedeemCode}
                  disabled={codeLoading}
                  className="px-5 py-3 rounded-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white disabled:opacity-50"
                >
                  {codeLoading ? <Loader2 className="animate-spin" size={18} /> : 'Canjear'}
                </button>
              </div>
            </div>
          )}

          {/* Only show "continue preview" if NOT at the limit */}
          {!isAtLimit && (
            <button onClick={onClose} className={`w-full py-2 mt-3 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Seguir con preview
            </button>
          )}
          
          {/* Security badge */}
          <p className={`text-center text-xs mt-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
            🔒 Pago seguro con Stripe
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Purchase Info Modal - for users who want to buy anytime
const PurchaseInfoModal = ({ onClose, theme, hasPaid, onDownload, onCodeSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const isDark = theme === 'dark';
  
  const handlePurchase = () => {
    const message = 'Quiero comprar el libro Kábala para principiantes Raíz lectura';
    const url = `https://wa.me/5492604801102?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleRedeemCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Ingresá un código');
      return;
    }
    setCodeLoading(true);
    try {
      const token = localStorage.getItem('reader_token');
      const response = await axios.post(`${BACKEND_URL}/api/reader/redeem-code`, 
        { code: promoCode.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success('🎉 ¡Código canjeado! Libro desbloqueado');
        if (onCodeSuccess) onCodeSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setCodeLoading(false);
    }
  };

  // If user already paid, show download options
  if (hasPaid) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
        <Card className={`max-w-md w-full relative animate-scale-in ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white'}`}>
          <button onClick={onClose} className={`absolute top-3 right-3 p-2 rounded-full ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <X size={18} />
          </button>
          <CardContent className="p-6">
            <div className="text-center mb-5">
              <div className="text-5xl mb-3">🎉</div>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ¡Ya tenés el libro completo!
              </h2>
              <p className={`text-base mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Podés descargar el PDF para leerlo en Kindle, tablet o cualquier dispositivo.
              </p>
            </div>
            
            <div className={`p-5 rounded-xl mb-5 ${isDark ? 'bg-green-900/20 border border-green-800' : 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'}`}>
              <h3 className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Opciones de lectura:</h3>
              <ul className={`space-y-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <div>
                    <strong>Descargar PDF</strong>
                    <p className="text-xs opacity-75">Para Kindle, Kobo, tablet o computadora</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <div>
                    <strong>Leer en esta página</strong>
                    <p className="text-xs opacity-75">Con modo oscuro, marcadores y progreso guardado</p>
                  </div>
                </li>
              </ul>
            </div>
            
            {/* iOS tip */}
            {/iPhone|iPad|iPod/i.test(navigator.userAgent) && (
              <p className={`text-xs text-center mb-3 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                📱 En iPhone: el PDF se abrirá en una nueva pestaña. Usá el botón de compartir para guardarlo.
              </p>
            )}
            
            <button 
              onClick={onDownload}
              data-testid="download-pdf-modal-btn"
              className="w-full py-4 rounded-full font-bold text-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-xl shadow-green-500/30 transition-all flex items-center justify-center gap-2"
            >
              <Download size={22} />
              Descargar PDF
            </button>
            
            <button 
              onClick={onClose} 
              className={`w-full py-3 mt-3 text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
            >
              Seguir leyendo en la web
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If user hasn't paid, show purchase options
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <Card className={`max-w-md w-full relative animate-scale-in ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white'}`}>
        <button onClick={onClose} className={`absolute top-3 right-3 p-2 rounded-full ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
          <X size={18} />
        </button>
        <CardContent className="p-6">
          <div className="text-center mb-5">
            <div className="text-5xl mb-3">📚</div>
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Adquirí el libro completo
            </h2>
            <p className={`text-base mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Desbloquea todas las páginas y descarga el PDF para leer donde quieras.
            </p>
          </div>
          
          <div className={`p-5 rounded-xl mb-5 ${isDark ? 'bg-amber-900/20 border border-amber-800' : 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200'}`}>
            <div className="flex justify-between items-center mb-4">
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Libro Completo</span>
              <div className="text-right">
                <span className="text-3xl font-bold text-amber-500">$15</span>
                <span className={`text-sm ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>USD</span>
              </div>
            </div>
            
            {/* Total pages highlight */}
            <div className={`text-center py-2 mb-3 rounded-lg ${isDark ? 'bg-amber-800/30' : 'bg-amber-100'}`}>
              <span className={`text-2xl font-bold ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>📖 176 páginas</span>
              <p className={`text-xs ${isDark ? 'text-amber-300/70' : 'text-amber-600'}`}>de conocimiento profundo sobre Cábala</p>
            </div>
            
            <ul className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <div>
                  <strong>Acceso a las 176 páginas</strong>
                  <p className="text-xs opacity-75">Lee en web con modo noche, marcadores y progreso</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <div>
                  <strong>Descarga en PDF</strong>
                  <p className="text-xs opacity-75">Para Kindle, Kobo, tablet o imprimir</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span>Soporte por WhatsApp</span>
              </li>
            </ul>
          </div>
          
          <button 
            onClick={handlePurchase} 
            data-testid="purchase-complete-btn"
            className="w-full py-4 rounded-full font-bold text-lg bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white shadow-xl shadow-green-500/30 transition-all flex items-center justify-center gap-2"
          >
            <MessageCircle size={22} /> Comprar por WhatsApp
          </button>

          {/* Promo Code Section */}
          {!showCodeInput ? (
            <button 
              onClick={() => setShowCodeInput(true)} 
              className={`w-full py-3 mt-4 text-sm font-medium ${isDark ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-700'}`}
            >
              🎁 ¿Tenés un código de regalo?
            </button>
          ) : (
            <div className="mt-4 space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="CÓDIGO"
                  data-testid="promo-code-input"
                  className={`flex-1 px-4 py-3 rounded-lg text-center font-mono uppercase tracking-wider border ${
                    isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-amber-500`}
                  maxLength={20}
                />
                <button
                  onClick={handleRedeemCode}
                  disabled={codeLoading}
                  data-testid="redeem-code-btn"
                  className="px-5 py-3 rounded-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white disabled:opacity-50"
                >
                  {codeLoading ? <Loader2 className="animate-spin" size={18} /> : 'Canjear'}
                </button>
              </div>
            </div>
          )}

          <button onClick={onClose} className={`w-full py-2 mt-3 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Seguir con preview gratuito
          </button>
          
          <p className={`text-center text-xs mt-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
            🔒 Pago seguro con Stripe
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
